import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useFormExitPrompt, FormExitDialog } from '../hooks/useFormExitPrompt';
import { CurrencyInput } from '../components/CurrencyInput';
import { 
  ShoppingBag, 
  DollarSign, 
  MapPin, 
  FileText,
  Loader2,
  ArrowLeft,
  ChevronDown,
  Navigation,
  Eye,
  CheckCircle,
  Sparkles,
  Info,
  Camera,
  X,
  AlertCircle,
  ImagePlus,
  Wand2,
  Link2,
  Globe,
  Laptop,
  Sofa,
  Shirt,
  Car,
  Dumbbell,
  Home,
  Palette,
  BookOpen,
  Wrench,
  Baby,
  Music,
  PawPrint,
  Package,
  ArrowUp,
  ArrowDown,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../lib/toast';
import axios from 'axios';

const CONDITIONS = [
  { id: 'like_new', label: 'Like New', description: 'Barely used, as good as new', color: 'text-[#059669]' },
  { id: 'excellent', label: 'Excellent', description: 'Light wear, great condition', color: 'text-[#059669]' },
  { id: 'good', label: 'Good', description: 'Normal wear, fully functional', color: 'text-[#C9956C]' },
  { id: 'fair', label: 'Fair', description: 'Shows wear but works fine', color: 'text-[#D97706]' },
  { id: 'poor/needs repair', label: 'Poor/Needs Repair', description: 'Heavy wear, may need repair', color: 'text-[#DC2626]' },
];

const RADIUS_OPTIONS = [2, 5, 10, 20, 30, 40, 50, 75, 100, 500];

const CATEGORY_ICONS = {
  electronics: Laptop,
  furniture: Sofa,
  clothing: Shirt,
  vehicles: Car,
  sports: Dumbbell,
  home: Home,
  collectibles: Palette,
  books: BookOpen,
  tools: Wrench,
  baby: Baby,
  music: Music,
  pets: PawPrint,
  other: Package
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateWTBPage() {
  const { api, token, user } = useAuth();
  const { location, requestLocation } = useLocation();
  const navigate = useNavigate();
  
  // Form exit prompt hook for unsaved changes warning
  const { setHasUnsavedChanges, blocker } = useFormExitPrompt();
  
  const [loading, setLoading] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationInput, setLocationInput] = useState(location.name);
  
  // Categories from API
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Image-first flow states
  const [imageAnalysing, setImageAnalysing] = useState(false);
  const [hasAnalysedImage, setHasAnalysedImage] = useState(false);
  const [imageKeywords, setImageKeywords] = useState([]);
  
  // URL image input state
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    min_price: '',
    max_price: '',
    is_firm: false,  // Changed from flexibility slider to boolean toggle
    condition_accepted: ['good', 'excellent', 'like_new'],
    search_radius_km: 50,
    images: [],  // Array of {ref, url, expires_at} objects
  });

  // Check if form has any data (is "dirty")
  const hasFormData = formData.title || formData.description || formData.category || 
    formData.min_price || formData.max_price || formData.images.length > 0;

  // Update unsaved changes state when form data changes
  useEffect(() => {
    setHasUnsavedChanges(hasFormData);
  }, [hasFormData, setHasUnsavedChanges]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    setLocationInput(location.name);
  }, [location.name]);

  // Update selectedCategory when formData.category changes
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const cat = categories.find(c => c.id === formData.category);
      setSelectedCategory(cat || null);
    }
  }, [formData.category, categories]);

  // Handle image upload (no auto-analysis)
  const handleImageUpload = async (files) => {
    const filesToUpload = Array.from(files).slice(0, 4 - formData.images.length);
    
    for (const file of filesToUpload) {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      try {
        const uploadResponse = await axios.post(
          `${API_URL}/api/upload/image`, 
          uploadFormData,
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data' 
            }
          }
        );
        
        // Store the full image object {ref, url, expires_at}
        // Also store a local blob URL for AI analysis (avoids CORS issues)
        let imageData;
        const localPreview = URL.createObjectURL(file);
        
        if (uploadResponse.data.image) {
          imageData = { ...uploadResponse.data.image, localPreview };
        } else {
          // Fallback for old format
          const ref = uploadResponse.data.url;
          const displayUrl = uploadResponse.data.signed_url || 
            (ref.startsWith('http') ? ref : `${API_URL}${ref}`);
          imageData = { ref, url: displayUrl, expires_at: null, localPreview };
        }
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, imageData]
        }));
        
        toast.success('Image uploaded');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || 'Failed to upload image');
      }
    }
  };

  // Handle adding image from URL
  const handleAddImageUrl = async () => {
    if (!imageUrlInput.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    // Basic URL validation
    try {
      const url = new URL(imageUrlInput.trim());
      if (!url.protocol.startsWith('http')) {
        toast.error('Please enter a valid HTTP/HTTPS URL');
        return;
      }
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    if (formData.images.length >= 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }
    
    setUrlLoading(true);
    
    try {
      // Fetch the image from URL to verify it's valid and accessible
      const response = await fetch(imageUrlInput.trim());
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.startsWith('image/')) {
        toast.error('URL does not point to a valid image');
        return;
      }
      
      // Convert to blob and upload to our server
      const blob = await response.blob();
      const uploadFormData = new FormData();
      uploadFormData.append('file', blob, 'url-image.jpg');
      
      const uploadResponse = await axios.post(
        `${API_URL}/api/upload/image`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const imageUrl = `${API_URL}${uploadResponse.data.url}`;
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
      
      setImageUrlInput('');
      toast.success('Image added from URL');
    } catch (error) {
      console.error('URL image error:', error);
      toast.error('Failed to load image from URL. Make sure the URL is accessible.');
    } finally {
      setUrlLoading(false);
    }
  };

  // Manual AI analysis of uploaded images
  const handleAnalyseImages = async () => {
    if (formData.images.length === 0) {
      toast.error('Please upload at least one image first');
      return;
    }
    
    setImageAnalysing(true);
    toast.info('Analysing images with AI...');
    
    try {
      // Get the first image - it's an object {ref, url, localPreview, expires_at}
      const imageData = formData.images[0];
      
      // Use localPreview (blob URL) if available - this is how AddInventory does it
      // This avoids CORS issues with GCS signed URLs
      const imageUrl = imageData.localPreview || imageData.url;
      
      if (!imageUrl) {
        throw new Error('No valid image URL found');
      }
      
      // Fetch from local blob URL (no CORS issues)
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      const blob = await response.blob();
      
      const analyzeFormData = new FormData();
      analyzeFormData.append('file', blob, 'image.jpg');
      
      const analysisResponse = await axios.post(
        `${API_URL}/api/upload/analyze-wtb-image`,
        analyzeFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const suggestion = analysisResponse.data;
      
      // Auto-fill form with AI suggestions
      setFormData(prev => ({
        ...prev,
        title: suggestion.title || prev.title,
        description: suggestion.description || prev.description,
        category: suggestion.category || prev.category,
        min_price: suggestion.suggested_min_price > 0 ? String(suggestion.suggested_min_price) : prev.min_price,
        max_price: suggestion.suggested_max_price > 0 ? String(suggestion.suggested_max_price) : prev.max_price,
      }));
      
      // Store the AI-generated keywords
      setImageKeywords(suggestion.semantic_keywords || []);
      setHasAnalysedImage(true);
      
      toast.success('AI suggestions applied! Feel free to edit.');
    } catch (analysisError) {
      console.error('Image analysis error:', analysisError);
      toast.error('Could not analyse image. Please fill in details manually.');
    } finally {
      setImageAnalysing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (parseFloat(formData.min_price) > parseFloat(formData.max_price)) {
      toast.error('Minimum price cannot be greater than maximum price');
      return;
    }

    if (formData.condition_accepted.length === 0) {
      toast.error('Please select at least one acceptable condition');
      return;
    }

    setLoading(true);

    try {
      // Images are already in the correct object format {ref, url, expires_at}
      const payload = {
        ...formData,
        min_price: parseFloat(formData.min_price),
        max_price: parseFloat(formData.max_price),
        location: { lat: location.lat, lng: location.lng },
        location_name: location.name,
        // Include AI-detected keywords for semantic matching
        image_keywords: imageKeywords.length > 0 ? imageKeywords : null,
      };

      const response = await api.post('/wtb/', payload);
      const wtbId = response.data.id;
      
      // Check for potential matches and notify user
      try {
        const matchesRes = await api.get(`/matches/wtb/${wtbId}/potential`);
        const potentialCount = matchesRes.data?.count || 0;
        
        if (potentialCount > 0) {
          toast.success(
            <div 
              className="cursor-pointer" 
              onClick={() => navigate(`/wtb/${wtbId}`)}
            >
              <strong>Your WANT ad is live!</strong>
              <p className="text-sm mt-1">🎯 {potentialCount} potential match{potentialCount > 1 ? 'es' : ''} found. Click to view!</p>
            </div>,
            { duration: 8000 }
          );
        } else {
          toast.success('Your WANT ad is live! We\'ll notify you when we find matches.');
        }
      } catch {
        toast.success('Your Want is live! We\'ll notify you when we find matches.');
      }
      
      navigate('/my-wants');
    } catch (error) {
      const errCode = error.response?.data?.detail?.error;
      if (errCode === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else if (errCode === 'account_suspended') {
        toast.error(error.response?.data?.detail?.message || 'Your account is suspended.');
      } else if (errCode === 'account_banned') {
        toast.error('Your account has been permanently banned.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create listing');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConditionChange = (conditionId, checked) => {
    setFormData(prev => ({
      ...prev,
      condition_accepted: checked
        ? [...prev.condition_accepted, conditionId]
        : prev.condition_accepted.filter(c => c !== conditionId),
    }));
  };

  // Move image up or down in the array (for reordering)
  const moveImage = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.images.length) return;
    
    const newImages = [...formData.images];
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleUseMyLocation = () => {
    requestLocation();
    setShowLocationDropdown(false);
  };

  // Get flexibility text for preview card
  const getFlexibilityText = () => formData.is_firm ? 'Firm' : 'Flexible';

  // Helper to get display URL from image (handles both old string and new object format)
  const getImageDisplayUrl = (img) => {
    if (typeof img === 'object' && img !== null) {
      return img.url || img.display || img.ref;
    }
    return img;
  };

  // Get the first image for preview
  const previewImage = formData.images.length > 0 
    ? getImageDisplayUrl(formData.images[0])
    : null;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-[#C9956C]" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                Post a Want
              </h1>
              <p className="text-[var(--theme-text-secondary)] mt-1">
                Tell us what you're looking for
              </p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form Column */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Step 1: IMAGE FIRST */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[var(--theme-border)]">
                  <div className="w-8 h-8 rounded-full bg-[#C9956C] flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <h2 className="font-bold text-[var(--theme-text-primary)] text-lg">Do you have an image of what you want?</h2>
                </div>

                {/* AI Feature Highlight */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-[#C9956C]/10 to-[#1E3A5F]/10 border border-[#C9956C]/20">
                  <Wand2 className="w-6 h-6 text-[#C9956C] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-[var(--theme-text-primary)] mb-1">AI-Powered Suggestions</h4>
                    <p className="text-sm text-[var(--theme-text-secondary)]">
                      Upload a reference image and our AI will suggest a title, description, category, and help match you with sellers! <span className="text-[var(--theme-text-muted)]">Note: Only the first image is analysed to generate the ad, however you may add more reference images to help describe your want.</span>
                    </p>
                  </div>
                </div>

                {/* Image Upload Area */}
                <div className="space-y-4">
                  <p className="text-sm text-[var(--theme-text-secondary)] flex items-start gap-2 bg-[var(--theme-hover)] p-3 rounded-lg">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C9956C]" />
                    <span>For best results, upload photos of a <strong>single item</strong> you're looking for. Create separate listings for different items.</span>
                  </p>
                  {formData.images.length === 0 ? (
                    <div className="space-y-4">
                      {/* Upload Option */}
                      <label className="block cursor-pointer">
                        <div className="border-2 border-dashed border-[var(--theme-border)] hover:border-[#C9956C] rounded-2xl p-8 text-center transition-colors bg-[var(--theme-hover)]">
                          {imageAnalysing ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-12 h-12 text-[#C9956C] animate-spin mb-4" />
                              <p className="text-[var(--theme-text-primary)] font-medium">Analysing your image...</p>
                              <p className="text-sm text-[var(--theme-text-secondary)] mt-1">AI is identifying the item and generating suggestions</p>
                            </div>
                          ) : (
                            <>
                              <ImagePlus className="w-12 h-12 text-[#C9956C] mx-auto mb-4" />
                              <p className="text-[var(--theme-text-primary)] font-medium">Drop an image here or click to upload</p>
                              <p className="text-sm text-[var(--theme-text-secondary)] mt-1">AI will analyse it and suggest your listing details</p>
                              <p className="text-xs text-[var(--theme-text-muted)] mt-3">JPG, PNG, WEBP • Max 4 images</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => handleImageUpload(e.target.files)}
                          disabled={imageAnalysing}
                          data-testid="wtb-image-upload"
                        />
                      </label>
                      
                      {/* OR Divider */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-[var(--theme-border)]"></div>
                        <span className="text-sm text-[var(--theme-text-muted)] font-medium">OR</span>
                        <div className="flex-1 h-px bg-[var(--theme-border)]"></div>
                      </div>
                      
                      {/* URL Input Option */}
                      <div className="p-4 rounded-xl bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                        <div className="flex items-center gap-2 mb-3">
                          <Globe className="w-5 h-5 text-[#1E3A5F]" />
                          <span className="font-medium text-[var(--theme-text-primary)]">Seen something you like online?</span>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="url"
                            value={imageUrlInput}
                            onChange={(e) => setImageUrlInput(e.target.value)}
                            placeholder="Paste the image URL here..."
                            className="flex-1 bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-11"
                            disabled={urlLoading}
                            data-testid="wtb-image-url-input"
                          />
                          <Button
                            type="button"
                            onClick={handleAddImageUrl}
                            disabled={urlLoading || !imageUrlInput.trim()}
                            className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white px-4 h-11"
                          >
                            {urlLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Link2 className="w-4 h-4 mr-2" />
                                Add
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-[var(--theme-text-muted)] mt-2">
                          Right-click an image online and select &quot;Copy image address&quot;
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-4 gap-3">
                        {formData.images.map((img, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-[var(--theme-border)] group">
                            <img src={getImageDisplayUrl(img)} alt={`Reference ${idx + 1}`} className="w-full h-full object-cover" />
                            
                            {/* Reorder buttons */}
                            <div className="absolute top-1 left-1 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              {idx > 0 && (
                                <button
                                  type="button"
                                  onClick={() => moveImage(idx, 'up')}
                                  className="w-5 h-5 rounded bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                                  title="Move left (make primary)"
                                >
                                  <ArrowUp className="w-3 h-3" />
                                </button>
                              )}
                              {idx < formData.images.length - 1 && (
                                <button
                                  type="button"
                                  onClick={() => moveImage(idx, 'down')}
                                  className="w-5 h-5 rounded bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                                  title="Move right"
                                >
                                  <ArrowDown className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            
                            {/* Delete button */}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== idx)
                                }));
                                if (idx === 0) {
                                  setHasAnalysedImage(false);
                                  setImageKeywords([]);
                                }
                              }}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            
                            {/* Primary badge */}
                            {idx === 0 && (
                              <div className="absolute bottom-1 left-1 px-2 py-0.5 rounded bg-[#1E3A5F]/90 text-white text-[10px] font-medium">
                                Primary
                              </div>
                            )}
                            {idx === 0 && hasAnalysedImage && (
                              <div className="absolute bottom-1 right-1 px-2 py-0.5 rounded bg-[#C9956C]/90 text-white text-xs flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                AI
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {formData.images.length < 4 && (
                          <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--theme-border)] hover:border-[#C9956C] transition-colors cursor-pointer flex flex-col items-center justify-center bg-[var(--theme-hover)]">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => handleImageUpload(e.target.files)}
                            />
                            <Camera className="w-6 h-6 text-[#9CA3AF] mb-1" />
                            <span className="text-xs text-[#9CA3AF]">Add More</span>
                          </label>
                        )}
                      </div>
                      
                      {/* URL Input for adding more images */}
                      {formData.images.length < 4 && (
                        <div className="flex gap-2">
                          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                            <Globe className="w-4 h-4 text-[var(--theme-text-muted)] flex-shrink-0" />
                            <Input
                              type="url"
                              value={imageUrlInput}
                              onChange={(e) => setImageUrlInput(e.target.value)}
                              placeholder="Or paste an image URL..."
                              className="flex-1 border-0 bg-transparent h-8 p-0 text-sm focus-visible:ring-0"
                              disabled={urlLoading}
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={handleAddImageUrl}
                            disabled={urlLoading || !imageUrlInput.trim()}
                            size="sm"
                            className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white h-10"
                          >
                            {urlLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Link2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {/* Analyze Button - Disabled for free users */}
                      {user?.is_premium ? (
                        <Button
                          type="button"
                          onClick={handleAnalyseImages}
                          disabled={imageAnalysing || formData.images.length === 0}
                          className="w-full rounded-full bg-gradient-to-r from-[#C9956C] to-[#1E3A5F] hover:from-[#B8845B] hover:to-[#162d4a] text-white h-12"
                          data-testid="analyse-images-btn"
                        >
                          {imageAnalysing ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Analysing Images...
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-5 h-5 mr-2" />
                              {hasAnalysedImage ? 'Re-Generate Ad Suggestions' : 'Generate Ad with AI'}
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            type="button"
                            disabled
                            className="w-full rounded-full bg-gray-300 text-gray-500 h-12 cursor-not-allowed"
                            data-testid="analyse-images-btn-disabled"
                          >
                            <Lock className="w-5 h-5 mr-2" />
                            Generate Ad with AI
                          </Button>
                          <p className="text-center text-sm text-[var(--theme-text-muted)]">
                            <a href="/subscribe" className="text-[#C9956C] hover:underline font-medium">Upgrade to WANTD+</a> to unlock AI-powered ad generation
                          </p>
                        </div>
                      )}
                      
                      {hasAnalysedImage && (
                        <p className="text-center text-sm text-[var(--theme-text-muted)]">
                          Not happy with the suggestions? Upload more images or click above to regenerate.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Skip image option */}
                  {formData.images.length === 0 && !imageAnalysing && (
                    <p className="text-center text-sm text-[var(--theme-text-muted)]">
                      No image? No problem — you can skip this and fill in details manually below.
                    </p>
                  )}
                </div>
              </div>

              {/* Step 2: Item Details */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[var(--theme-border)]">
                  <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <h2 className="font-bold text-[var(--theme-text-primary)] text-lg">What are you looking for?</h2>
                  {hasAnalysedImage && (
                    <span className="ml-auto text-xs px-2 py-1 rounded-full bg-[#C9956C]/10 text-[#C9956C] flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      AI Suggested
                    </span>
                  )}
                </div>
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                    Item Title <span className="text-[#DC2626]">*</span>
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., iPhone 15 Pro Max 256GB"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-12 text-base"
                    required
                    data-testid="wtb-title-input"
                  />
                  <p className="text-xs text-[#9CA3AF] mt-1.5">Be specific - include brand, model, size, color if relevant</p>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                    Category <span className="text-[#DC2626]">*</span>
                  </label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value, subcategory: '' }))}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-12 text-[var(--theme-text-primary)]" data-testid="wtb-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {categories.map((cat) => {
                        const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                        return (
                          <SelectItem key={cat.id} value={cat.id} className="hover:bg-[var(--theme-hover)]">
                            <span className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4" />
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subcategory - Only show if category has subcategories */}
                {selectedCategory?.subcategories?.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                      Subcategory <span className="text-[var(--theme-text-muted)]">(optional)</span>
                    </label>
                    <Select 
                      value={formData.subcategory || ''} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory: value }))}
                    >
                      <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-12 text-[var(--theme-text-primary)]" data-testid="wtb-subcategory-select">
                        <SelectValue placeholder="Select a subcategory (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                        {selectedCategory.subcategories.map((sub) => (
                          <SelectItem key={sub.id} value={sub.id} className="hover:bg-[var(--theme-hover)]">
                            {sub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                    Description <span className="text-[#DC2626]">*</span>
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the specific features, specs, colors, or must-haves you're looking for..."
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] min-h-[120px] text-base"
                    required
                    data-testid="wtb-description-input"
                  />
                  <p className="text-xs text-[#9CA3AF] mt-1.5">The more detail you provide, the better our AI can match you with sellers</p>
                </div>
              </div>

              {/* Step 3: Your Budget */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[var(--theme-border)]">
                  <div className="w-8 h-8 rounded-full bg-[#059669] flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                  <h2 className="font-bold text-[var(--theme-text-primary)] text-lg">Your Budget</h2>
                </div>
                
                {/* Price Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                      Minimum <span className="text-[#DC2626]">*</span>
                    </label>
                    <CurrencyInput
                      value={formData.min_price}
                      onChange={(value) => setFormData(prev => ({ ...prev, min_price: value }))}
                      placeholder="0"
                      className="h-12 text-lg font-semibold"
                      required
                      dataTestId="wtb-min-price-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                      Maximum <span className="text-[#DC2626]">*</span>
                    </label>
                    <CurrencyInput
                      value={formData.max_price}
                      onChange={(value) => setFormData(prev => ({ ...prev, max_price: value }))}
                      placeholder="1,000"
                      className="h-12 text-lg font-semibold"
                      required
                      dataTestId="wtb-max-price-input"
                    />
                  </div>
                </div>

                {/* Flexibility Toggle */}
                <div className="pt-4">
                  <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-3">
                    Price Flexibility
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_firm: true }))}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold transition-all ${
                        formData.is_firm
                          ? 'border-[#DC2626] bg-[#DC2626]/5 text-[#DC2626]'
                          : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#D4CCC2]'
                      }`}
                    >
                      Firm Price
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_firm: false }))}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold transition-all ${
                        !formData.is_firm
                          ? 'border-[#059669] bg-[#059669]/5 text-[#059669]'
                          : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#D4CCC2]'
                      }`}
                    >
                      Flexible
                    </button>
                  </div>
                  <p className="text-xs text-[var(--theme-text-muted)] mt-2">
                    {formData.is_firm 
                      ? 'You prefer offers close to your stated budget range'
                      : 'You\'re open to negotiation outside your budget range'}
                  </p>
                </div>
              </div>

              {/* Step 4: Acceptable Conditions */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[var(--theme-border)]">
                  <div className="w-8 h-8 rounded-full bg-[#C9956C] flex items-center justify-center text-white font-bold text-sm">
                    4
                  </div>
                  <h2 className="font-bold text-[var(--theme-text-primary)] text-lg">Acceptable Conditions</h2>
                </div>
                
                <p className="text-sm text-[var(--theme-text-secondary)]">Select all conditions you&apos;d accept. More options = more matches!</p>
                
                {/* Improved conditions layout - horizontal pills */}
                <div className="flex flex-wrap gap-2">
                  {CONDITIONS.map((condition) => {
                    const isChecked = formData.condition_accepted.includes(condition.id);
                    return (
                      <button
                        key={condition.id}
                        type="button"
                        onClick={() => handleConditionChange(condition.id, !isChecked)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full border-2 cursor-pointer transition-all ${
                          isChecked 
                            ? 'border-[#1E3A5F] bg-[#1E3A5F]/5' 
                            : 'border-[var(--theme-border)] hover:border-[#D4CCC2]'
                        }`}
                      >
                        {isChecked && <CheckCircle className="w-4 h-4 text-[#059669]" />}
                        <span className={`font-medium text-sm ${isChecked ? 'text-[var(--theme-text-primary)]' : 'text-[var(--theme-text-secondary)]'}`}>
                          {condition.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 5: Location & Radius */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[var(--theme-border)]">
                  <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-bold text-sm">
                    5
                  </div>
                  <h2 className="font-bold text-[var(--theme-text-primary)] text-lg">Search Location</h2>
                </div>
                
                {/* Location Input with button */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                    Your Location
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-3 h-12 px-4 bg-[var(--theme-hover)] border border-[var(--theme-border)] rounded-xl">
                      <MapPin className="w-5 h-5 text-[#C9956C] flex-shrink-0" />
                      <Input
                        type="text"
                        placeholder="Enter suburb, postcode..."
                        value={locationInput}
                        onChange={(e) => setLocationInput(e.target.value)}
                        className="flex-1 border-0 bg-transparent h-10 p-0 text-[var(--theme-text-primary)] focus-visible:ring-0"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleUseMyLocation}
                      variant="outline"
                      className="h-12 px-4 border-[var(--theme-border)] hover:border-[#C9956C] hover:bg-[#C9956C]/5"
                    >
                      <Navigation className="w-4 h-4 text-[#C9956C] mr-2" />
                      <span className="hidden sm:inline">Use my location</span>
                      <span className="sm:hidden">GPS</span>
                    </Button>
                  </div>
                </div>

                {/* Search Radius */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-[var(--theme-text-primary)]">
                      Search Radius
                    </label>
                    <span className="font-bold text-[#C9956C]">{formData.search_radius_km} km</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {RADIUS_OPTIONS.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, search_radius_km: r }))}
                        className={`py-2 px-1 text-sm rounded-lg border transition-all ${
                          formData.search_radius_km === r
                            ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]'
                            : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] border-[var(--theme-border)] hover:border-[#1E3A5F]'
                        }`}
                      >
                        {r}km
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button 
                  type="button"
                  variant="outline"
                  className="flex-1 border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-hover)] rounded-full h-14"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-[2] bg-[#C9956C] hover:bg-[#B8845B] rounded-full h-14 text-lg font-semibold"
                  disabled={loading || imageAnalysing}
                  data-testid="wtb-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Post My WANT Ad
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                <span className="text-sm font-medium text-[var(--theme-text-secondary)]">Live Preview</span>
              </div>
              
              {/* Preview Card - Matching WTBCard style */}
              <div className="rounded-xl border border-[var(--theme-border)] overflow-hidden bg-[var(--theme-card)]">
                {/* Preview Image - Square aspect ratio */}
                <div className="relative aspect-square bg-[var(--theme-hover)]">
                  {previewImage ? (
                    <img 
                      src={previewImage} 
                      alt="Reference preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#9CA3AF]">
                      <Camera className="w-12 h-12 mb-2 opacity-40" />
                      <span className="text-sm">No reference image</span>
                    </div>
                  )}
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#1E3A5F]/90 text-white">
                      WANTED
                    </span>
                  </div>
                  {/* Views Badge */}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px]">
                    <Eye className="w-3 h-3" />
                    <span>0</span>
                  </div>
                </div>

                {/* Preview Content - Matching WTBCard style */}
                <div className="p-2.5 space-y-2">
                  {/* Title */}
                  <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 leading-tight">
                    {formData.title || 'Your item title'}
                  </h3>

                  {/* Price + Flexibility */}
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-[#059669]">
                      {formData.min_price && formData.max_price 
                        ? `$${parseInt(formData.min_price).toLocaleString()} - $${parseInt(formData.max_price).toLocaleString()}`
                        : '$0 - $0'}
                    </span>
                    <span className={`text-[10px] font-medium uppercase ${formData.is_firm ? 'text-[#DC2626]' : 'text-[#059669]'}`}>
                      {getFlexibilityText()}
                    </span>
                  </div>

                  {/* Conditions */}
                  {formData.condition_accepted.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {formData.condition_accepted.slice(0, 3).map((cond) => (
                        <span key={cond} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#F5F0E8] text-[9px] text-[var(--theme-text-secondary)] capitalize">
                          <CheckCircle className="w-2.5 h-2.5 text-[#059669]" />
                          {cond.replace('_', ' ')}
                        </span>
                      ))}
                      {formData.condition_accepted.length > 3 && (
                        <span className="text-[9px] text-[var(--theme-text-muted)]">
                          +{formData.condition_accepted.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Location */}
                  <div className="flex items-center justify-between pt-2 border-t border-[var(--theme-border)] text-[11px] text-[var(--theme-text-muted)]">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{location.name}</span>
                    </div>
                    <span>{formData.search_radius_km}km</span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 rounded-xl bg-[#1E3A5F]/5 border border-[#1E3A5F]/10">
                <h4 className="font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9956C]" />
                  Tips for Better Matches
                </h4>
                <ul className="space-y-2 text-sm text-[var(--theme-text-secondary)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                    Upload a reference image for AI-powered matching
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                    Be specific about brand, model, and color
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                    Accept more conditions for more matches
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Form Exit Dialog - triggered by React Router blocker */}
      <FormExitDialog
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onDiscard={() => {
          setHasUnsavedChanges(false);
          blocker.proceed?.();
        }}
      />
    </Layout>
  );
}
